import { ExternalLink } from "lucide-react";

import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import type { OtcOrder } from "../../types/cryptoSwaps.types";
import { shortenAddress } from "../../utils/cryptoSwapAddress";
import { AssetAmount } from "../shared/AssetAmount";
import { CopyValueButton, DetailRow, MonoValue } from "../shared/CryptoSwapBits";
import { FeesValue } from "../shared/FeesValue";
import { NetworkName } from "../shared/NetworkIcon";

function Money({ value, currency, strong }: { value: string; currency: string; strong?: boolean }) {
  return (
    <MonoValue className={strong ? "font-semibold" : undefined}>
      <AssetAmount
        value={value}
        currency={currency}
        size="sm"
        className={strong ? "font-semibold" : "font-normal"}
      />{" "}
      {currency}
    </MonoValue>
  );
}

export interface OrderDetailsListProps {
  order: OtcOrder;
}

/** Everything the order is, as one description list. Shared by the order page and the history drawer. */
export function OrderDetailsList({ order }: OrderDetailsListProps) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const format = useAmountFormatter();
  const txUrl = labels.txUrl(order.destination.network, order.destination.tx_hash);
  const explorer = labels.networkRule(order.destination.network)?.explorer ?? "";
  const date = (value: string) => (
    <MonoValue className="text-xs">{formatDateTime(value, { locale: activeLanguage })}</MonoValue>
  );

  return (
    <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5">
      <DetailRow label={t("summary_debited")}>
        <Money value={order.send.amount} currency={order.send.currency} />
      </DetailRow>
      <DetailRow label={t("summary_received")}>
        <Money value={order.receive.amount} currency={order.receive.currency} strong />
      </DetailRow>
      <DetailRow label={t("summary_price")}>
        <MonoValue>
          {t("price_line", {
            asset: order.receive.currency,
            price: format(order.price.value, order.price.currency),
            currency: order.price.currency,
          })}
        </MonoValue>
      </DetailRow>
      <DetailRow label={t("summary_fees")}>
        <FeesValue fees={order.fees} />
      </DetailRow>
      <DetailRow label={t("summary_network")}>
        <NetworkName network={order.destination.network} full />
      </DetailRow>
      <DetailRow label={t("summary_address")}>
        {order.destination.address ? (
          <span className="inline-flex items-center justify-end gap-1">
            <MonoValue breakAll className="text-xs">
              {shortenAddress(order.destination.address)}
            </MonoValue>
            <CopyValueButton value={order.destination.address} fieldLabel={t("copy_address")} size="xs" />
          </span>
        ) : (
          "—"
        )}
      </DetailRow>
      <DetailRow label={t("summary_tx_hash")}>
        {order.destination.tx_hash ? (
          <span className="inline-flex flex-wrap items-center justify-end gap-1">
            <MonoValue breakAll className="text-xs">
              {shortenAddress(order.destination.tx_hash, 8)}
            </MonoValue>
            <CopyValueButton value={order.destination.tx_hash} fieldLabel={t("copy_tx_hash")} size="xs" />
            {txUrl && explorer && (
              <a
                href={txUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                {t("summary_view_explorer", { explorer })}
                <ExternalLink aria-hidden="true" className="size-3" />
              </a>
            )}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t("summary_tx_pending")}</span>
        )}
      </DetailRow>
      {order.concept && <DetailRow label={t("summary_concept")}>{order.concept}</DetailRow>}
      <DetailRow label={t("summary_created")}>{date(order.created_at)}</DetailRow>
      {order.paid_at && <DetailRow label={t("summary_paid_at")}>{date(order.paid_at)}</DetailRow>}
      {order.failed_at && <DetailRow label={t("summary_failed_at")}>{date(order.failed_at)}</DetailRow>}
      <DetailRow label={t("summary_order_id")}>
        <span className="inline-flex items-center justify-end gap-1">
          <MonoValue breakAll className="text-xs text-muted-foreground">
            {order.id}
          </MonoValue>
          <CopyValueButton value={order.id} fieldLabel={t("copy_order_id")} size="xs" />
        </span>
      </DetailRow>
    </dl>
  );
}
